import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './MediaDisplay.module.css';

const MediaDisplay = ({ project, projectIndex }) => {
  const [currentImage, setCurrentImage] = useState(0);
  const [videoProgress, setVideoProgress] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const timerRef = useRef(null);
  const mediaElementRef = useRef(null);

  const handleImageChange = useCallback(
    (newIndex) => {
      // Clear existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      setCurrentImage(newIndex);

      // Start new timer for the new image
      startImageTimer(newIndex);
    }, []
  );

  const startImageTimer = useCallback(
    (currentIdx) => {
      if (!project?.media?.length) return;

      const media = project.media[currentIdx];
      const duration = media.type === 'video' ? 0 : 4000;

      if (duration > 0) {
        // Clear existing timer if any
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }

        // Set new timer
        timerRef.current = setTimeout(() => {
          handleImageChange((currentIdx + 1) % project.media.length);
        }, duration);
      }
    },
    [project, handleImageChange]
  );

  const handleVideoTimeUpdate = (event) => {
    const video = event.target;
    const progress = (video.currentTime / video.duration) * 100;
    setVideoProgress(progress);
  };

  useEffect(() => {
    // Start timer for initial image
    startImageTimer(currentImage);

    // Cleanup function
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [project, currentImage, startImageTimer]);

  if (!project.media?.length) return null;

  const media = project.media[currentImage];

  return (
    <div
      className={styles.mediaContainer}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={styles.projectMediaWrapper}>
        {media.type === 'video' ? (
          <video
            key={media.src}
            ref={mediaElementRef}
            src={media.src}
            autoPlay
            muted
            playsInline
            className={styles.projectMedia}
            onEnded={() =>
              handleImageChange((currentImage + 1) % project.media.length)
            }
            onTimeUpdate={handleVideoTimeUpdate}
          />
        ) : (
          <img
            key={media.src}
            ref={mediaElementRef}
            src={media.src}
            alt={`${project.title} showcase`}
            className={styles.projectMedia}
          />
        )}

        {project.media.length > 1 && (
          <div className={styles.mediaControls}>
            <button
              className={`${styles.navButton} ${styles.prev}`}
              onClick={(e) => {
                e.stopPropagation();
                const newIdx =
                  (currentImage - 1 + project.media.length) %
                  project.media.length;
                handleImageChange(newIdx);
              }}
            >
              <span className="material-symbols-outlined mini">
                chevron_left
              </span>
            </button>

            <div className={styles.dots}>
              {project.media.map((_, index) => (
                <span
                  key={index}
                  className={`${styles.dot} ${
                    index === currentImage ? styles.active : ''
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleImageChange(index);
                  }}
                >
                  {index === currentImage && (
                    <div
                      className={`${styles.progress} ${
                        media.type === 'image' ? styles.imageProgress : ''
                      }`}
                      style={{
                        transform:
                          media.type === 'video'
                            ? `scaleX(${videoProgress / 100 || 0})`
                            : undefined,
                      }}
                    />
                  )}
                </span>
              ))}
            </div>

            <button
              className={`${styles.navButton} ${styles.next}`}
              onClick={(e) => {
                e.stopPropagation();
                const newIdx = (currentImage + 1) % project.media.length;
                handleImageChange(newIdx);
              }}
            >
              <span className="material-symbols-outlined mini">
                chevron_right
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaDisplay;
